import { useState } from 'react';
import { useLoaderData, useNavigate } from 'react-router';
import { paths } from '../../../common/constants/pathVariables.js';
import { ShoppingBasketCoCoScope } from '../../../common/enums.js';
import { parseBasketJson, useShoppingBasketCoCo } from '../../../common/hooks/useShoppingBasketCoCo.js';
import { LastItemRemovalDialog } from '../../../components/PlaceOrder/LastItemRemovalDialog.js';
import { PlaceOrderCoCo } from '../../../components/PlaceOrder/PlaceOrderCoCo.js';
import { PlanProductRuntimeCharacteristic } from '../../../components/PlaceOrder/utils/characteristicsFlagsUtilsCoCo.js';
import {
  applyQuantityChangeToBasket,
  removeEntriesByCartItemIds,
  updateAddonGroupSelectionInBasket,
  updateAddonInBasket,
  updatePlanProductOfferingSpecRuntimeCharacteristicInBasket,
  updateProductOfferingRuntimeCharacteristicInBasket,
} from '../../../components/ShoppingBasket/shoppingBasketHelpersCoCo.js';
import { useAuth } from '../AuthProvider.js';
import type { PlaceProductOrderLoaderData } from '../../../common/loaders.js';
import type { ProductOfferingCartItems, ShoppingBasketType } from '../../../common/types/shoppingBasketCoCo.js';

export const PlaceOrderPathCoCo = ({ isEmployee = false }: { isEmployee?: boolean } = {}) => {
  const { authenticatedUser } = useAuth();
  const { mdmId, customerInfo, productOffersInCart, contacts, billChannels } =
    useLoaderData() as PlaceProductOrderLoaderData;
  const { shoppingBasket, setShoppingBasket } = useShoppingBasketCoCo(
    isEmployee ? ShoppingBasketCoCoScope.EMPLOYEE : ShoppingBasketCoCoScope.ADMIN
  );
  const shoppingCartJson: ShoppingBasketType = parseBasketJson(shoppingBasket);
  const [pendingRemovalItems, setPendingRemovalItems] = useState<ProductOfferingCartItems[] | undefined>();
  const [isLastItemModalVisible, setIsLastItemModalVisible] = useState(false);
  const navigate = useNavigate();

  const changeQuantity = (newQty: number, groupItems: ProductOfferingCartItems[]) => {
    // Show confirmation dialog if removing this group would empty the cart
    const totalEntries = (shoppingCartJson.productOfferings || []).length;
    const wouldEmptyCart = totalEntries <= groupItems.length && newQty === 0;

    if (wouldEmptyCart) {
      setPendingRemovalItems(groupItems);
      setIsLastItemModalVisible(true);
      return;
    }

    setShoppingBasket(JSON.stringify(applyQuantityChangeToBasket(shoppingCartJson, newQty, groupItems)));
  };

  const updateProductAddons = (addonId: string, groupCartItemIds: string[]) => {
    setShoppingBasket(JSON.stringify(updateAddonInBasket(shoppingCartJson, addonId, groupCartItemIds)));
  };

  // Fiber mode is edited again in checkout, so reuse the basket updater the cart step already relies on.
  const updateProductOfferingRuntimeCharacteristic = (groupCartItemIds: string[], selectedValue: string) => {
    setShoppingBasket(
      JSON.stringify(
        updateProductOfferingRuntimeCharacteristicInBasket(shoppingCartJson, groupCartItemIds, selectedValue)
      )
    );
  };

  const updatePlanProductOfferingSpecRuntimeCharacteristic = (cartItemId: string, selectedValue: string) => {
    setShoppingBasket(
      JSON.stringify(
        updatePlanProductOfferingSpecRuntimeCharacteristicInBasket(
          shoppingCartJson,
          [cartItemId],
          selectedValue,
          PlanProductRuntimeCharacteristic.DIRECTORY_LISTING_PUBLICITY
        )
      )
    );
  };

  const updateAddonGroupSelection = (groupId: string, selectedOfferingIds: string[], groupCartItemIds: string[]) => {
    setShoppingBasket(
      JSON.stringify(
        updateAddonGroupSelectionInBasket(shoppingCartJson, groupCartItemIds, groupId, selectedOfferingIds)
      )
    );
  };

  const shoppingBasketUpdateMethods = {
    changeQuantity,
    updateProductAddons,
    updateProductOfferingRuntimeCharacteristic,
    updatePlanProductOfferingSpecRuntimeCharacteristic,
    updateAddonGroupSelection,
  };
  const removeLastCartItem = () => {
    if (pendingRemovalItems) {
      const toRemoveIds = pendingRemovalItems.map(item => item.cartItemId);
      setShoppingBasket(JSON.stringify(removeEntriesByCartItemIds(shoppingCartJson, toRemoveIds)));
      setIsLastItemModalVisible(false);
      setPendingRemovalItems(undefined);
      navigate(isEmployee ? paths.EMPLOYEE_SHOPPING_CART : paths.SHOPPING_BASKET);
    }
  };

  if (!authenticatedUser) {
    return null;
  }

  return (
    <>
      <PlaceOrderCoCo
        shoppingBasket={shoppingCartJson}
        shoppingBasketUpdateMethods={shoppingBasketUpdateMethods}
        user={authenticatedUser}
        mdmId={mdmId}
        selectedProductOffers={productOffersInCart}
        customerInfo={customerInfo}
        initialContacts={contacts.contacts || []}
        billChannels={billChannels}
        isEmployeeOrder={isEmployee}
      />
      <LastItemRemovalDialog
        isVisible={isLastItemModalVisible}
        onCancel={() => {
          setIsLastItemModalVisible(false);
          setPendingRemovalItems(undefined);
        }}
        onConfirm={removeLastCartItem}
      />
    </>
  );
};
